import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Car,
  User,
  Phone,
  Mail,
  CreditCard,
  Hash,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileText,
  Package,
  RotateCcw,
  ArrowRight,
  Building2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Banknote,
  Receipt,
  Flag,
} from "lucide-react";
import api from "../api/Api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (
  d,
  opts = { day: "numeric", month: "short", year: "numeric" },
) => (d ? new Date(d).toLocaleDateString("en-IN", opts) : "—");
const fmtCurrency = (v) =>
  v != null && v !== "" ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

// ─── BULLETPROOF TIME FORMATTER ───────────────────────────────────────────────
const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "—";

  const dateObj = new Date(dateStr);
  const formattedDate = dateObj.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  let formattedTime = "";
  if (timeStr) {
    const timeString = String(timeStr);
    if (timeString.includes("T")) {
      const timeObj = new Date(timeString);
      formattedTime = timeObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      const cleanTime = timeString
        .padStart(4, "0")
        .replace(/(\d{2})(\d{2})/, "$1:$2");
      const [hours, minutes] = cleanTime.split(":");
      if (hours !== undefined && minutes !== undefined) {
        const tempDate = new Date();
        tempDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        formattedTime = tempDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    }
  }

  return formattedTime ? `${formattedDate} · ${formattedTime}` : formattedDate;
};

// ─── DYNAMIC STATUS CHECKER ───────────────────────────────────────────────────
const getDynamicStatus = (dateStr, timeStr, enqStatus, isCancelled) => {
  if (isCancelled === 1) return 5; // Cancelled
  if (enqStatus === 2) return 2;
  if (enqStatus === 3) return 3;
  if (enqStatus === 0) return 0;

  if (enqStatus === 1) {
    let tripDateTime = new Date(dateStr);
    if (timeStr) {
      const timeString = String(timeStr);
      if (timeString.includes("T")) {
        const timeObj = new Date(timeString);
        tripDateTime.setHours(timeObj.getHours(), timeObj.getMinutes(), 0, 0);
      } else {
        const formattedTime = timeString
          .padStart(4, "0")
          .replace(/(\d{2})(\d{2})/, "$1:$2");
        const [hours, minutes] = formattedTime.split(":");
        if (hours !== undefined && minutes !== undefined) {
          tripDateTime.setHours(
            parseInt(hours, 10),
            parseInt(minutes, 10),
            0,
            0,
          );
        }
      }
    }
    const now = new Date();
    if (now >= tripDateTime) return 4; // Ongoing
    return 1; // Confirmed
  }
  return enqStatus;
};

// ─── Enquiry status from your DB (Updated with Ongoing & Cancelled) ───────────
const STATUS = {
  0: { label: "Pending", bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  1: { label: "Confirmed", bg: "#dcfce7", color: "#14532d", dot: "#22c55e" },
  2: { label: "Expired", bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" },
  3: { label: "Revised", bg: "#ede9fe", color: "#5b21b6", dot: "#8b5cf6" },
  4: { label: "Ongoing", bg: "#fef08a", color: "#854d0e", dot: "#eab308" }, // New Ongoing Status
  5: { label: "Cancelled", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" }, // New Cancelled Status
};

const PAY_TYPE = { 1: "Full Payment", 2: "Partial Payment", 3: "Pay Later" };

// ─── Cancel reasons ───────────────────────────────────────────────────────────
const CANCEL_REASONS = [
  "Change of travel plans",
  "Found a better option",
  "Emergency / personal reason",
  "Booking made by mistake",
  "Driver not assigned yet",
  "Other",
];

// ─────────────────────────────────────────────────────────────────────────────
// Reusable UI
// ─────────────────────────────────────────────────────────────────────────────
const Card = ({ children, style }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 22,
      padding: 18,
      border: "1px solid #f0f0f0",
      boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
      marginBottom: 14,
      ...style,
    }}
  >
    {children}
  </div>
);

const SLabel = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      color: "#94a3b8",
      letterSpacing: 1.6,
      textTransform: "uppercase",
      marginBottom: 12,
    }}
  >
    {children}
  </div>
);

const InfoRow = ({ icon: Icon, label, value, color, last }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "10px 0",
      borderBottom: last ? "none" : "1px solid #f8fafc",
    }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        background: "#f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={15} color={color || "#64748b"} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#94a3b8",
          letterSpacing: 1,
          textTransform: "uppercase",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "#0f172a",
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </div>
    </div>
  </div>
);

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS[status] ?? STATUS[0];
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: s.bg,
        borderRadius: 50,
        padding: "5px 12px",
      }}
    >
      <div
        style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }}
      />
      <span style={{ fontSize: 11, fontWeight: 700, color: s.color }}>
        {s.label}
      </span>
    </div>
  );
};

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ msg, type }) => (
  <div
    style={{
      position: "fixed",
      bottom: 100,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 99999,
      background: type === "error" ? "#fef2f2" : "#f0fdf4",
      border: `1.5px solid ${type === "error" ? "#fecaca" : "#bbf7d0"}`,
      borderRadius: 14,
      padding: "12px 20px",
      fontSize: 13,
      fontWeight: 700,
      color: type === "error" ? "#dc2626" : "#15803d",
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      animation: "toastIn 0.3s ease",
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    {type === "error" ? <XCircle size={15} /> : <CheckCircle size={15} />}
    {msg}
  </div>
);

// ─── Cancel Bottom Sheet ──────────────────────────────────────────────────────
const CancelSheet = ({ enq, onConfirm, onClose, loading }) => {
  const [reason, setReason] = useState("");
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 10000,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "28px 28px 0 0",
          padding: "20px 20px 36px",
          width: "100%",
          maxWidth: 480,
          animation: "slideUp 0.28s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 4,
            background: "#e2e8f0",
            margin: "0 auto 20px",
          }}
        />

        {/* Icon */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#fef2f2",
            border: "2px solid #fecaca",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 14px",
          }}
        >
          <AlertTriangle size={26} color="#f43f5e" />
        </div>

        <h3
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: "#0f172a",
            margin: "0 0 6px",
            textAlign: "center",
          }}
        >
          Request Cancellation?
        </h3>
        <p
          style={{
            fontSize: 13,
            color: "#64748b",
            margin: "0 0 20px",
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          Sending cancellation request for <strong>{enq?.enqref_no}</strong>.
          <br />
          Our team will review and confirm.
        </p>

        {/* Reasons */}
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#94a3b8",
              letterSpacing: 1.4,
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            Select Reason
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {CANCEL_REASONS.map((r) => (
              <div
                key={r}
                onClick={() => setReason(r)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 14px",
                  borderRadius: 14,
                  border: `1.5px solid ${reason === r ? "#f43f5e" : "#e2e8f0"}`,
                  background: reason === r ? "#fef2f2" : "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: `2px solid ${reason === r ? "#f43f5e" : "#cbd5e1"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {reason === r && (
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#f43f5e",
                      }}
                    />
                  )}
                </div>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: reason === r ? "#dc2626" : "#334155",
                  }}
                >
                  {r}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 14,
              background: "#f1f5f9",
              border: "none",
              fontSize: 14,
              fontWeight: 700,
              color: "#475569",
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            Keep Booking
          </button>
          <button
            onClick={() => reason && onConfirm(reason)}
            disabled={!reason || loading}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 14,
              background:
                reason && !loading
                  ? "linear-gradient(135deg,#dc2626,#f43f5e)"
                  : "#f1f5f9",
              border: "none",
              fontSize: 14,
              fontWeight: 700,
              color: reason && !loading ? "#fff" : "#94a3b8",
              cursor: reason && !loading ? "pointer" : "not-allowed",
              fontFamily: "'DM Sans',sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              transition: "all 0.2s",
            }}
          >
            {loading ? (
              <>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid rgba(255,255,255,0.35)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    animation: "spin 0.7s linear infinite",
                    display: "inline-block",
                  }}
                />{" "}
                Sending...
              </>
            ) : (
              <>
                <XCircle size={15} /> Send Request
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function EnquiryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("customerToken");

  const [enq, setEnq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showFare, setShowFare] = useState(false);
  const [showDriver, setShowDriver] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch enquiry details ──────────────────────────────────────────────────
  const { confirmationId } = useParams();
  const lookupId = id || confirmationId;
  const lookupType = confirmationId ? "confirmation" : "enquiry";

  const fetchEnquiry = useCallback(
    async (silent = false) => {
      silent ? setRefreshing(true) : setLoading(true);
      try {
        const endpoint =
          lookupType === "confirmation"
            ? `/bookingRoutes/enquiryByConfirmation/${lookupId}`
            : `/bookingRoutes/enquiryDetails/${lookupId}`;

        const res = await api.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEnq(res.data?.data || res.data);
      } catch {
        showToast("Could not load enquiry details", "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [lookupId, lookupType, token],
  );
  useEffect(() => {
    fetchEnquiry();
  }, [fetchEnquiry]);

  // ── Cancel request ────────────────────────────────────────────────────────
  const handleCancel = async (reason) => {
    setCancelling(true);
    try {
      await api.post(
        "/bookingRoutes/cancelEnquiry",
        { enq_id: enq.enq_id, reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowCancel(false);
      showToast("Cancellation request sent!");
      fetchEnquiry(true);
    } catch {
      showToast("Failed to send cancellation request", "error");
    } finally {
      setCancelling(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f4f4f8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'DM Sans',sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 44,
              height: 44,
              border: "3px solid #f0f0f0",
              borderTopColor: "#ff7a00",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 14px",
            }}
          />
          <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>
            Loading enquiry...
          </div>
        </div>
      </div>
    );

  if (!enq)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f4f4f8",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          fontFamily: "'DM Sans',sans-serif",
        }}
      >
        <FileText size={44} color="#cbd5e1" />
        <h3 style={{ margin: 0, color: "#0f172a" }}>Enquiry not found</h3>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#0f172a",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            padding: "10px 24px",
            fontWeight: 700,
            fontFamily: "'DM Sans',sans-serif",
            cursor: "pointer",
          }}
        >
          Go Back
        </button>
      </div>
    );

  const due_amnt = (enq?.final_price || 0) - (enq?.adv_amnt || 0);
  const isCancelled = enq.is_cancelled === 1;
  const canCancel =
    !isCancelled && enq.enq_status !== 2 && enq.trip_closed !== 1;

  // Evaluate Dynamic Status for Header Badge & Status Section
  const currentStatus = getDynamicStatus(
    enq.arrive_dt,
    enq.arrive_time,
    enq.enq_status,
    enq.is_cancelled,
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f4f4f8",
        fontFamily: "'DM Sans',sans-serif",
        paddingBottom: 100,
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes toastIn { from{opacity:0;transform:translateY(16px) translateX(-50%)} to{opacity:1;transform:translateY(0) translateX(-50%)} }
        .press { transition:transform 0.12s,opacity 0.12s; cursor:pointer; }
        .press:active { transform:scale(0.96)!important; opacity:0.8; }
      `}</style>

      {toast && <Toast msg={toast.msg} type={toast.type} />}

      {/* ── HEADER ──────────────────────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(160deg,#fff1d6 0%,#163e35 100%)",
          borderRadius: "0 0 30px 30px",
          padding: "52px 20px 24px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: "rgba(255,122,0,0.1)",
            pointerEvents: "none",
          }}
        />

        {/* Title row */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                fontSize: 19,
                fontWeight: 900,
                color: "#fff",
                margin: "0 0 4px",
                letterSpacing: -0.3,
              }}
            >
              Enquiry Details
            </h1>
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.4)",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Hash size={11} /> {enq.enqref_no || `ENQ-${id}`}
            </div>
          </div>
          <StatusBadge status={currentStatus} />
        </div>
      </div>

      {/* ── BODY ────────────────────────────────────────────────────────────── */}
      <div
        style={{ padding: "20px 16px 0", animation: "fadeUp 0.4s ease both" }}
      >
        {/* Cancelled banner */}
        {isCancelled && (
          <div
            style={{
              background: "#fef2f2",
              borderRadius: 16,
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 14,
              border: "1.5px solid #fecaca",
            }}
          >
            <XCircle size={20} color="#f43f5e" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#dc2626" }}>
                Cancellation Requested
              </div>
              <div style={{ fontSize: 12, color: "#f87171" }}>
                Our team will process your request shortly.
              </div>
            </div>
          </div>
        )}

        {/* ─── 1. TRIP ROUTE ─────────────────────────────────────────────────── */}
        <Card>
          <SLabel>Trip Route</SLabel>

          {/* Pickup */}
          <div style={{ display: "flex", gap: 12 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 4,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#22c55e",
                  boxShadow: "0 0 0 3px rgba(34,197,94,0.15)",
                }}
              />
              <div
                style={{
                  width: 2,
                  flex: 1,
                  minHeight: 40,
                  background: `repeating-linear-gradient(to bottom,#ff7a00 0,#ff7a00 4px,transparent 4px,transparent 8px)`,
                  margin: "4px 0",
                  opacity: 0.35,
                }}
              />
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: "#f43f5e",
                  boxShadow: "0 0 0 3px rgba(244,63,94,0.15)",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#22c55e",
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    marginBottom: 2,
                  }}
                >
                  Pickup
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.4,
                  }}
                >
                  {enq.pickup_address || enq.pickup_city || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
                  {/* Using Dynamic Date Formatter */}
                  {formatDateTime(enq.arrive_dt, enq.arrive_time)}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#f43f5e",
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    marginBottom: 2,
                  }}
                >
                  Drop Off
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.4,
                  }}
                >
                  {enq.drop_address || enq.drop_location || "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Return date (if present) */}
          {enq.return_date && (
            <div
              style={{
                marginTop: 12,
                background: "#fff7ed",
                borderRadius: 12,
                padding: "10px 14px",
                border: `1px solid #fed7aa`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <RotateCcw size={13} color="#ff7a00" />
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: "#ff7a00" }}
                >
                  Return Date
                </span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {/* Using Dynamic Date Formatter */}
                {formatDateTime(enq.return_date, enq.return_time)}
              </span>
            </div>
          )}

          {/* Duration / Package (if present) */}
          {(enq.duration || enq.taxi_package) && (
            <div
              style={{
                marginTop: 12,
                background: "#fff7ed",
                borderRadius: 12,
                padding: "10px 14px",
                border: `1px solid #fed7aa`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Package size={13} color="#ff7a00" />
                <span
                  style={{ fontSize: 12, fontWeight: 700, color: "#ff7a00" }}
                >
                  {enq.taxi_package ? "Package" : "Duration"}
                </span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {enq.taxi_package || enq.duration}
              </span>
            </div>
          )}
        </Card>

        {/* ─── 2. CUSTOMER DETAILS ─────────────────────────────────────────── */}
        <Card>
          <SLabel>Customer Details</SLabel>
          <InfoRow icon={User} label="Name" value={enq.fullname} />
          <InfoRow
            icon={Phone}
            label="Mobile"
            value={enq.mob}
            color="#22c55e"
          />
          <InfoRow
            icon={Mail}
            label="Email"
            value={enq.mail}
            color="#6366f1"
            last
          />
          {enq.notes && (
            <InfoRow
              icon={FileText}
              label="Special Request"
              value={enq.notes}
              last
            />
          )}
        </Card>

        {/* ─── 3. VEHICLE ──────────────────────────────────────────────────── */}
        {enq.booked_car && (
          <Card>
            <SLabel>Booked Vehicle</SLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: "linear-gradient(135deg,#1e293b,#0f172a)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Car size={22} color="#a5b4fc" />
              </div>
              <div>
                <div
                  style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}
                >
                  {enq.booked_car}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  Selected vehicle
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* ─── 4. DRIVER (if assigned) ─────────────────────────────────────── */}
        {enq.driver_name && (
          <Card>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                marginBottom: showDriver ? 4 : 0,
              }}
              onClick={() => setShowDriver((d) => !d)}
            >
              <SLabel>Assigned Driver</SLabel>
              {showDriver ? (
                <ChevronUp
                  size={16}
                  color="#94a3b8"
                  style={{ marginBottom: 12 }}
                />
              ) : (
                <ChevronDown
                  size={16}
                  color="#94a3b8"
                  style={{ marginBottom: 12 }}
                />
              )}
            </div>
            {showDriver && (
              <>
                <InfoRow
                  icon={User}
                  label="Driver Name"
                  value={enq.driver_name}
                />
                <InfoRow
                  icon={Phone}
                  label="Driver Mobile"
                  value={enq.driver_mobile}
                  color="#22c55e"
                />
                {enq.vehicle_no && (
                  <InfoRow
                    icon={Car}
                    label="Vehicle No"
                    value={enq.vehicle_no}
                  />
                )}
                {enq.car_name && (
                  <InfoRow
                    icon={Car}
                    label="Car Model"
                    value={enq.car_name}
                    last
                  />
                )}
                {enq.driver_mobile && (
                  <a
                    href={`tel:${enq.driver_mobile}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      marginTop: 14,
                      padding: "11px",
                      borderRadius: 14,
                      background: "#dcfce7",
                      border: "1px solid #bbf7d0",
                      textDecoration: "none",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#15803d",
                    }}
                  >
                    <Phone size={14} /> Call Driver
                  </a>
                )}
              </>
            )}
          </Card>
        )}

        {/* ─── 5. FARE & PAYMENT ───────────────────────────────────────────── */}
        <Card>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={() => setShowFare((f) => !f)}
          >
            <SLabel>Fare & Payment</SLabel>
            {showFare ? (
              <ChevronUp
                size={16}
                color="#94a3b8"
                style={{ marginBottom: 12 }}
              />
            ) : (
              <ChevronDown
                size={16}
                color="#94a3b8"
                style={{ marginBottom: 12 }}
              />
            )}
          </div>

          {/* Total always shown */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 0",
              marginBottom: showFare ? 10 : 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                }}
              >
                <Banknote size={15} />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                Total Amount
              </span>
            </div>
            <span style={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>
              {fmtCurrency(enq.final_price)}
            </span>
          </div>

          {showFare && (
            <>
              <div
                style={{
                  height: 1,
                  background: "#f1f5f9",
                  margin: "4px 0 8px",
                }}
              />
              <InfoRow
                icon={CreditCard}
                label="Payment Type"
                value={PAY_TYPE[enq.adv_type] || "—"}
              />
              <InfoRow
                icon={Banknote}
                label="Amount Paid"
                value={fmtCurrency(enq.adv_amnt)}
                color="#22c55e"
              />
              <InfoRow
                icon={Receipt}
                label="Due Amount"
                value={fmtCurrency(due_amnt)}
                color="#f59e0b"
              />
              {enq.confirmation_id && (
                <InfoRow
                  icon={Hash}
                  label="Confirmation ID"
                  value={enq.confirmation_id}
                  last
                />
              )}
            </>
          )}
        </Card>

        {/* ─── 6. BOOKING META ─────────────────────────────────────────────── */}
        <Card>
          <SLabel>Booking Information</SLabel>
          <InfoRow icon={Hash} label="Enquiry Ref" value={enq.enqref_number} />
          <InfoRow
            icon={Calendar}
            label="Booked On"
            value={fmtDate(enq.enq_createddate)}
          />
          <InfoRow
            icon={Flag}
            label="Status"
            value={STATUS[currentStatus]?.label}
            color={STATUS[currentStatus]?.dot}
            last
          />
        </Card>

        {/* ─── 7. ACTION BUTTON ────────────────────────────────────────────── */}
        {canCancel ? (
          <button
            className="press"
            onClick={() => setShowCancel(true)}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: 18,
              background: "#fff",
              border: "1.5px solid #fecaca",
              color: "#dc2626",
              fontSize: 15,
              fontWeight: 800,
              fontFamily: "'DM Sans',sans-serif",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              boxShadow: "0 2px 12px rgba(244,63,94,0.08)",
            }}
          >
            <XCircle size={18} /> Request Cancellation
          </button>
        ) : (
          <div
            style={{
              background: "#f8fafc",
              borderRadius: 18,
              padding: "16px",
              textAlign: "center",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}>
              {isCancelled
                ? "✓ Cancellation already requested"
                : "This booking cannot be cancelled"}
            </div>
          </div>
        )}
      </div>

      {/* ── CANCEL BOTTOM SHEET ─────────────────────────────────────────────── */}
      {showCancel && (
        <CancelSheet
          enq={enq}
          onConfirm={handleCancel}
          onClose={() => setShowCancel(false)}
          loading={cancelling}
        />
      )}
    </div>
  );
}